/* Owns transient notifications: the store, the `useToast()` hook and the
   viewport. A module-level store rather than context state so a mutation
   callback in a feature `api.ts` can raise a toast without a hook. */

import { AlertTriangle, CheckCircle2, Info, XCircle, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useSyncExternalStore, type ReactNode } from 'react'

import { cn } from './cn.js'

export type ToastVariant = 'default' | 'success' | 'warn' | 'danger'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  variant: ToastVariant
  title: string
  description?: string
  durationMs?: number
  action?: ToastAction
}

export type ToastInput = Omit<Toast, 'id'> & { id?: string }

const DEFAULT_DURATION_MS = 5_000
/** A danger toast usually carries a recovery hint, so it stays longer. */
const DANGER_DURATION_MS = 8_000
const MAX_STACKED = 3

let toasts: Toast[] = []
const listeners = new Set<() => void>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function snapshot(): Toast[] {
  return toasts
}

export function dismissToast(id: string): void {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
  toasts = toasts.filter((toast) => toast.id !== id)
  emit()
}

export function pushToast(input: ToastInput): string {
  const id = input.id ?? `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const duration = input.durationMs ?? (input.variant === 'danger' ? DANGER_DURATION_MS : DEFAULT_DURATION_MS)
  const toast: Toast = { ...input, id }
  // Oldest first out: three stacked toasts already hide the page corner.
  toasts = [...toasts, toast].slice(-MAX_STACKED)
  emit()
  timers.set(
    id,
    setTimeout(() => dismissToast(id), duration),
  )
  return id
}

export function clearToastsForTests(): void {
  for (const timer of timers.values()) clearTimeout(timer)
  timers.clear()
  toasts = []
  emit()
}

export function useToast(): {
  toast: (input: ToastInput) => string
  dismiss: (id: string) => void
  toasts: Toast[]
} {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  return { toast: pushToast, dismiss: dismissToast, toasts: current }
}

const ICONS: Record<ToastVariant, LucideIcon> = {
  default: Info,
  success: CheckCircle2,
  warn: AlertTriangle,
  danger: XCircle,
}

const TONE: Record<ToastVariant, string> = {
  default: 'border-border-strong',
  success: 'border-ok',
  warn: 'border-warn',
  danger: 'border-danger',
}

const ICON_TONE: Record<ToastVariant, string> = {
  default: 'text-text-muted',
  success: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
}

export function ToastViewport(): ReactNode {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot)
  const hasDanger = current.some((toast) => toast.variant === 'danger')

  return (
    <div
      // A danger toast interrupts; everything else waits for a pause.
      aria-live={hasDanger ? 'assertive' : 'polite'}
      aria-atomic="false"
      className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2"
    >
      {current.map((toast) => {
        const Icon = ICONS[toast.variant]
        return (
          <div
            key={toast.id}
            role={toast.variant === 'danger' ? 'alert' : 'status'}
            data-testid={`toast-${toast.variant}`}
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-lg border bg-elevated p-3 shadow-pop',
              TONE[toast.variant],
            )}
          >
            <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', ICON_TONE[toast.variant])} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-sm font-medium text-text">{toast.title}</p>
              {toast.description ? <p className="text-xs text-text-muted">{toast.description}</p> : null}
              {toast.action ? (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick()
                    dismissToast(toast.id)
                  }}
                  className="self-start text-xs font-medium text-accent hover:underline"
                >
                  {toast.action.label}
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(toast.id)}
              aria-label="close"
              className="rounded p-0.5 text-text-faint hover:text-text"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
